<template>
  <div
    class="d-flex justify-content-between align-items-center"
    style="border-bottom: 0.5px solid #0000004D; padding-bottom: 0.25rem;"
  >
    <!-- Tabs -->
    <div class="position-relative d-flex gap-3 gap-md-4">
      <Button
        v-for="tab in tabs"
        :key="tab"
        @click="setActiveTab(tab)"
        variant="naked"
        class="tab-control position-relative"
        :style="{ color: modelValue === tab ? 'var(--ch-black)' : '#00000080' }"
        :disabled="modelValue === tab"
      >
        {{ tab }}
        <span
          v-if="modelValue === tab"
          class="active-underline"
        />
      </Button>
    </div>

    <!-- Search -->
    <div v-if="showSearch" class="position-relative">
      <div
        :style="{
          maxWidth: searchInputFocus || query ? '10rem' : '5.5rem',
          color: '#00000080',
          transition: 'all 0.3s ease-in-out'
        }"
      >
        <Input
          v-model="query"
          type="text"
          placeholder="Search..."
          @focus="setSearchInputFocus(true)"
          @blur="setSearchInputFocus(false)"
          variant="naked"
          :icon="SearchIcon"
          :input-class="['py-2', searchInputFocus ? 'pe-4' : 'pe-0']"
        />
      </div>

      <Button
        v-if="query"
        @click="clearSearch"
        icon="CloseIcon"
        variant="naked"
        class="position-absolute end-0 top-50 translate-middle-y text-muted pe-2"
      >
        <CancelIcon width="8" height="8" />
      </Button>
    </div>
  </div>


</template>

<script lang="ts" setup>
import { ref, watch } from "vue";

import { Button, Input } from "@/components/global";
import { CancelIcon, SearchIcon } from "@/components/svg";

import { useDebounce } from "@/composables";

const props = defineProps<{
  tabs: string[];
  modelValue: string;
  showSearch?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "update:search", value: string): void;
  (e: "change", payload: { tab: string; search: string }): void;
}>();

const query = ref<string>("");
const searchInputFocus = ref<boolean>(false);
const debouncedQuery = useDebounce(query);


watch(debouncedQuery, (val) => {
  emit("update:search", val);
  emit("change", { tab: props.modelValue, search: val });
});

const setActiveTab = (tab: string) => {
  emit("update:modelValue", tab);
  emit("change", { tab, search: query.value });
}

const clearSearch = () => {
  query.value = "";
}

const setSearchInputFocus = (value: boolean) => {
  searchInputFocus.value = value;
}
</script>

<style scoped>
.tab-control:disabled {
  opacity: 1;
}

.active-underline {
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(var(--font-xs) * -1.15);
  height: 0.1875rem;
  background-color: var(--ch-blue-100);
  border-radius: 0.625rem 0.625rem 0 0;
}
</style>